<template>
  <SelectButton
    v-bind.prop="props"
    v-model="modelValue"
    v-on="listeners"
    :pt="passThrough"
    :class="[sizeClass]"
    :unstyled="true"
    class="meridia-split-button"
  >
    <template v-slot:option="slotData">
      <slot name="option" :option="slotData.option" :index="slotData.index" />
    </template>
  </SelectButton>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { SplitButtonProps, SplitButtonSizes } from './types';
import { SelectButtonPassThroughOptions } from 'primevue';
import { PassThrough } from '@primevue/core';
import { usePassThrough } from 'primevue/passthrough';
import SelectButton, {
  SelectButtonEmitsOptions,
  SelectButtonSlots,
} from 'primevue/selectbutton';
import { useListeners } from '../../utils';

const modelValue = defineModel<any>();
const props = withDefaults(defineProps<SplitButtonProps>(), {
  size: SplitButtonSizes.SMALL,
});

const emit = defineEmits<{
  (event: 'update:modelValue', value: any): void;
  (event: 'change', value: any): void;
  (event: 'focus', e: Event): void;
  (event: 'blur', e: Event): void;
}>();

defineSlots<SelectButtonSlots>();

const { listeners } = useListeners<SelectButtonEmitsOptions>(
  emit,
  SelectButton
);

const sizeClass = computed(() => {
  return {
    'meridia-split-button-small': props.size === SplitButtonSizes.SMALL,
    'meridia-split-button-big': props.size === SplitButtonSizes.BIG,
  };
});

const passThrough = usePassThrough(
  props.pt,
  {
    pcToggleButton: {
      root: {
        class: 'meridia-split-button-toggle-button',
      },
    },
  } as SelectButtonPassThroughOptions,
  { mergeSections: false, mergeProps: true }
) as PassThrough<SelectButtonPassThroughOptions>;
</script>

<style lang="scss">
.meridia-split-button {
  @apply rounded-[6px] p-0.5 bg-neutral-surface-default border border-primary-border-subtle inline-flex divide-x divide-neutral-border-subtle;

  .meridia-split-button-toggle-button {
    @apply inline-flex justify-center items-center gap-3xs flex-shrink bg-neutral-surface-default text-neutral-text-subtitle transition-all duration-200;
  }
  .meridia-split-button-toggle-button:hover {
    @apply bg-primary-surface-muted text-primary-label-darker;
  }
  .meridia-split-button-toggle-button[data-p-checked='true'] {
    @apply bg-primary-surface-subtle text-primary-label-elevated;
  }

  .meridia-split-button-toggle-button:first-child {
    @apply rounded-l-[4px];
  }
  .meridia-split-button-toggle-button:last-child {
    @apply rounded-r-[4px];
  }

  &.meridia-split-button-small {
    .meridia-split-button-toggle-button {
      @apply py-1.5 px-xs text-body-small;
    }
  }
  &.meridia-split-button-big {
    .meridia-split-button-toggle-button {
      @apply py-xs px-s text-body-base;
    }
  }

  .meridia-split-button-toggle-button[data-p-disabled='true'] {
    @apply cursor-not-allowed;
  }
}
</style>
